import { initializeFaro, getWebInstrumentations, FetchTransport } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { SpanStatusCode } from '@opentelemetry/api';

let faroInstance = null;

export function initFaro(router) {
  // 只在有設定 FARO_URL 時啟用
  if (!process.env.VUE_APP_OTEL_EXPORTER_OTLP_ENDPOINT) {
    console.log('[Faro] Skipped: VUE_APP_OTEL_EXPORTER_OTLP_ENDPOINT not set');
    return null;
  }

  try {
    const faroUrl = process.env.NODE_ENV === 'development'
      ? '/faro/collect'
      : `${process.env.VUE_APP_OTEL_EXPORTER_OTLP_ENDPOINT}/collect`;

    const transportOptions = {
      url: faroUrl,
      requestOptions: {
        headers: {
          ...(process.env.VUE_APP_FARO_API_KEY && {
            'X-Api-Key': process.env.VUE_APP_FARO_API_KEY,
          }),
        },
      },
    };

    faroInstance = initializeFaro({
      transports: [new FetchTransport(transportOptions)],
      app: {
        name: 'takeorder-admin',
        version: process.env.VUE_APP_VERSION || '2.5.1',
        environment: process.env.VUE_APP_DEPLOYMENT_ENVIRONMENT_NAME || 'development',
      },
      instrumentations: [
        // 基本的 Web Vitals、錯誤、console 追蹤
        ...getWebInstrumentations({
          captureConsole: true,
          captureConsoleDisabledLevels: ['debug', 'trace'], // 排除 debug/trace
        }),
        // Distributed Tracing
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: [
              // Specify URLs for cross-origin trace header propagation
              /localhost/,
              new RegExp(new URL(process.env.VUE_APP_API_URI).hostname),
            ],
            fetchInstrumentationOptions: {
              // 排除不需要追蹤的外部請求 (如 Google Analytics 等), 避免 signals 噪音
              ignoreUrls: [
                // 排除 Faro 自身的 telemetry 推送，避免自我追蹤迴圈
                new RegExp(faroUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
                /^https:\/\/www\.google\.com/,
                /^https:\/\/analytics\.google\.com/,
                /^https:\/\/www\.googletagmanager\.com/,
                /^https:\/\/www\.google-analytics\.com/,
                /^https:\/\/www\.googleadservices\.com/,
                /^https:\/\/googleads\.g\.doubleclick\.net/,
              ],
            },
          },
        }),
      ],
    });

    // 監聽路由變化，記錄頁面瀏覽
    if (router) {
      router.afterEach((to) => {
        if (faroInstance) {
          faroInstance.api.pushEvent('page_view', {
            route: to.path,
            routeName: to.name || 'unknown',
          });
        }
      });
    }

    console.log('[Faro] Initialized successfully');
    return faroInstance;
  } catch (error) {
    console.error('[Faro] Failed to initialize:', error);
    return null;
  }
}

export function getFaro() {
  return faroInstance;
}

// 方便手動記錄錯誤
export function logError(error, context = {}) {
  if (faroInstance) {
    faroInstance.api.pushError(error, { context });
  }
}

// 方便記錄自定義事件
export function logEvent(name, attributes = {}) {
  if (faroInstance) {
    faroInstance.api.pushEvent(name, attributes);
  }
}

/**
 * 手動建立 traced operation，讓 logs 可以關聯到 trace context
 * 適用於需要在錯誤處理中記錄 log 並關聯 trace 的場景
 *
 * @example
 * await withTracedOperation('fetchUserData', async ({ traceId, spanId }) => {
 *   const res = await fetch('/api/user');
 *   if (!res.ok) {
 *     logError(new Error('Failed to fetch user'), { trace_id: traceId, span_id: spanId });
 *   }
 * });
 */
export async function withTracedOperation(operationName, callback) {
  if (!faroInstance) {
    // Faro 未啟用，直接執行 callback
    return callback({ traceId: null, spanId: null });
  }

  const tracer = faroInstance.api.getOTEL()?.trace?.getTracer('takeorder-admin');
  if (!tracer) {
    return callback({ traceId: null, spanId: null });
  }

  return tracer.startActiveSpan(operationName, async (span) => {
    try {
      const spanContext = span.spanContext();
      const result = await callback({
        traceId: spanContext.traceId,
        spanId: spanContext.spanId,
      });
      span.end();
      return result;
    } catch (error) {
      span.recordException(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
      span.end();
      throw error;
    }
  });
}
